import styled from "styled-components";
import {fontFamilies, fontWeights, topbar} from "theme/tokens";

/**
 * Shared chrome for the crawlable, in-flow public content pages (ui#97, wave 2): About, Features and
 * the legal routes (/terms, /privacy, /cookies). One place owns the topbar clearance, the readable
 * measure and the token-based prose typography, so every text page reads the same in light and dark.
 *
 * SSG-safe and hydration-safe: pure presentational styled-components, no browser APIs, no portals —
 * the markup lands verbatim in the pre-rendered HTML for crawlers.
 */

/**
 * The page surface. A real `<main>` landmark that clears the absolute floating {@link topbar} (its
 * height is the shared token, so clearance can never drift from the bar) and caps the line length.
 * `$wide` widens the measure for pages with a comparison table (Features); the default suits prose.
 */
export const ContentSurface = styled.main<{$wide?: boolean}>`
	position: relative;
	z-index: 1;
	width: 100%;
	max-width: ${({$wide}) => ($wide ? "1040px" : "760px")};
	margin: 0 auto;
	box-sizing: border-box;
	padding: calc(${topbar.height} + 28px) 24px 72px;
	text-align: left;
	color: ${({theme}) => theme.tokens.ink2};
	font-family: ${fontFamilies.sans};

	@media (max-width: 640px) {
		padding: calc(${topbar.heightMobile} + 20px) 18px 56px;
	}
`;

/**
 * Token-based typography for a block of semantic HTML. The legal content components emit bare
 * `h2/h3/p/ul/li/strong/a/code/table` (no styling of their own — see {@link ../legal/PolicyDialog}),
 * so this styles their descendants; the About page reuses it for its own semantic markup. Colors,
 * fonts and rules all come from `theme.tokens`, so the same content follows light/dark mode.
 */
export const Prose = styled.div`
	font-size: 15.5px;
	line-height: 1.68;
	color: ${({theme}) => theme.tokens.ink2};

	h1 {
		margin: 0 0 8px;
		font-family: ${fontFamilies.displaySerif};
		font-weight: ${fontWeights.display};
		/* Restored with the display face (ui#1257) from the clamp(28px, 4.2vw, 35px) ui#1219 measured
		   for a face that set ~1.5x wider. Read that re-derivation precisely: it preserved each
		   title's ORIGINAL line count, which for the longest Romanian article titles is two at desktop
		   width, not one. Same face and same ramp reproduce the same wrapping — verified by rendering
		   every prerendered page in both languages at 1440/901/375 and comparing line counts. */
		font-size: clamp(34px, 5vw, 50px);
		line-height: 1.04;
		letter-spacing: -0.015em;
		color: ${({theme}) => theme.tokens.ink};
	}

	h2 {
		margin: 0 0 10px;
		font-weight: 700;
		font-size: clamp(24px, 3.2vw, 30px);
		letter-spacing: -0.015em;
		line-height: 1.15;
		color: ${({theme}) => theme.tokens.ink};
	}

	h3 {
		margin: 30px 0 8px;
		font-weight: 700;
		font-size: 18px;
		letter-spacing: -0.01em;
		color: ${({theme}) => theme.tokens.ink};
	}

	h4 {
		margin: 22px 0 6px;
		font-weight: 700;
		font-size: 15.5px;
		color: ${({theme}) => theme.tokens.ink};
	}

	p {
		margin: 0 0 14px;
	}

	ul,
	ol {
		margin: 0 0 16px;
		padding-left: 22px;
	}

	li {
		margin: 0 0 8px;
	}

	strong {
		color: ${({theme}) => theme.tokens.ink};
		font-weight: 700;
	}

	a {
		color: ${({theme}) => theme.tokens.linkInk};
		text-decoration: underline;
		text-underline-offset: 0.16em;
	}

	a:hover {
		text-decoration: underline;
	}

	a:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
		border-radius: 3px;
	}

	code {
		font-family: ${fontFamilies.mono};
		font-size: 0.86em;
		background: ${({theme}) => theme.tokens.field};
		border: 1px solid ${({theme}) => theme.tokens.fieldLine};
		border-radius: 5px;
		padding: 1px 5px;
	}

	/* The Cookie Policy's inventory table — already wrapped in an overflow-x scroller by the shared
	   component, so a phone never scrolls the page sideways. */
	table {
		width: 100%;
		border-collapse: collapse;
		margin: 0 0 16px;
		font-size: 13.5px;
	}

	th,
	td {
		border: 1px solid ${({theme}) => theme.tokens.line};
		padding: 9px 12px;
		text-align: left;
		vertical-align: top;
	}

	th {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink};
		font-weight: 700;
	}
`;

/**
 * Keyboard-reachable horizontal scroller for comparison tables that exceed a narrow viewport. The
 * caller supplies the localized table-section heading as its accessible name.
 */
export const HorizontalTableRegion = styled.div`
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior-inline: contain;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: -2px;
	}
`;

/** Readable comparison width that intentionally hands narrow viewports to HorizontalTableRegion. */
export const WideComparisonTable = styled.table`
	min-width: 34rem;
`;
